/**
 * useChat Hook
 * Manages conversation with transcription context
 */

import { useState, useCallback } from 'react';
import { createConversation, sendMessage, generateSummary } from '../services/api.service';

export function useChat() {
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [transcription, setTranscription] = useState('');

  /**
   * Initialize conversation with transcription
   */
  const startConversation = useCallback(async (text, fileMetadata = {}) => {
    setLoading(true);
    setError(null);
    setTranscription(text);

    try {
      const result = await createConversation(text, fileMetadata);
      setConversationId(result.data.conversationId);
      
      // Add welcome message with transcription preview
      const preview = text.length > 500 
        ? text.substring(0, 500) + '...' 
        : text;
      
      setMessages([
        {
          role: 'assistant',
          content: `¡Hola! He analizado la transcripción. ¿Qué te gustaría saber?\n\n📄 **Vista previa de la transcripción:**\n> ${preview.replace(/\n/g, '\n> ')}\n\nPuedes pedirme:\n• Un resumen ejecutivo\n• Puntos clave\n• Tareas accionables\n• O hacer cualquier pregunta sobre el contenido`,
          timestamp: Date.now()
        }
      ]);

      return result.data.conversationId;
    } catch (err) {
      setError(err.message || 'Failed to start conversation');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Send message and get response
   */
  const sendChatMessage = useCallback(async (message) => {
    if (!conversationId) {
      setError('No active conversation');
      return;
    }

    setLoading(true);
    setError(null);

    // Add user message immediately
    const userMessage = { role: 'user', content: message, timestamp: Date.now() };
    setMessages(prev => [...prev, userMessage]);

    try {
      const result = await sendMessage(conversationId, message);
      
      const assistantMessage = {
        role: 'assistant',
        content: result.data.message,
        timestamp: Date.now(),
        metadata: result.data.metadata
      };

      setMessages(prev => [...prev, assistantMessage]);
      return result.data;
    } catch (err) {
      setError(err.message || 'Failed to send message');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [conversationId]);

  /**
   * Generate quick summary
   */
  const getQuickSummary = useCallback(async (type = 'executive') => {
    if (!transcription) {
      setError('No transcription available');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await generateSummary(transcription, type);
      
      const summaryMessage = {
        role: 'assistant',
        content: `**Resumen (${type}):**\n\n${result.data.summary}`,
        timestamp: Date.now(),
        metadata: { tokensUsed: result.data.tokensUsed }
      };

      setMessages(prev => [...prev, summaryMessage]);
      return result.data;
    } catch (err) {
      setError(err.message || 'Failed to generate summary');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [transcription]);

  const reset = useCallback(() => {
    setConversationId(null);
    setMessages([]);
    setLoading(false);
    setError(null);
    setTranscription('');
  }, []);

  return {
    conversationId,
    messages,
    loading,
    error,
    transcription,
    startConversation,
    sendChatMessage,
    getQuickSummary,
    reset
  };
}
